
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock } from "lucide-react";
import { MockBadge } from "@/components/ui/mock-badge";

interface TimeData {
  hour: number;
  opens: number;
}

interface TimeAnalyticsProps {
  timeData?: TimeData[];
  isLoading: boolean;
}

export function TimeAnalytics({ timeData, isLoading }: TimeAnalyticsProps) {
  // Mock data for development until phase 4 integration is complete
  const mockTimeData = Array.from({ length: 24 }, (_, i) => {
    // Create a distribution that peaks in morning and evening
    let opens = 0;
    if (i >= 6 && i <= 10) { // Morning peak
      opens = 30 + Math.floor(Math.random() * 50);
    } else if (i >= 17 && i <= 21) { // Evening peak
      opens = 40 + Math.floor(Math.random() * 60);
    } else {
      opens = 5 + Math.floor(Math.random() * 25);
    }
    return { hour: i, opens };
  });

  const data = timeData || mockTimeData;
  
  const chartData = {
    labels: data.map(item => `${item.hour}:00`),
    datasets: [
      {
        label: 'Opens by Hour',
        data: data.map(item => item.opens),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  // Find peak hours (top 3)
  const peakHours = [...data]
    .sort((a, b) => b.opens - a.opens)
    .slice(0, 3)
    .map(item => ({
      hour: item.hour,
      opens: item.opens,
      formatted: `${item.hour}:00 - ${item.hour + 1}:00`
    }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">Optimal Send Times</CardTitle>
            <CardDescription>When your subscribers are most active</CardDescription>
          </div>
          <div className="flex gap-2">
            <MockBadge 
              phase="4" 
              tooltip="Time analytics currently uses simulated data. Actual time optimization will be implemented in Phase 4."
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="w-full h-[250px]" />
        ) : (
          <div className="space-y-4">
            <div className="h-[250px]">
              <LineChart data={chartData} />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-2">Peak Activity Hours</h4>
              <div className="grid grid-cols-3 gap-2">
                {peakHours.map((peak, index) => (
                  <div key={index} className="flex items-center p-2 border rounded-md">
                    <Clock className="h-4 w-4 text-indigo-500 mr-2" />
                    <div>
                      <p className="text-xs font-medium">{peak.formatted}</p>
                      <p className="text-xs text-gray-500">{peak.opens} opens</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
